import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import type { RootState } from "../../../app/store";
import { PaginatedResult } from "../../sessions/contracts/PaginatedResult";
import { OccurrenceTopicResponse } from "../contracts/OccurrenceTopicResponse";
import { ProgrammeResponse } from "../contracts/ProgrammeResponse";
import { ProgrammeSummaryResponse } from "../contracts/ProgrammeSummaryResponse";
import { ProgressResponse } from "../contracts/ProgressResponse";
import { SessionTopicPlanResponse } from "../contracts/SessionTopicPlanResponse";
import { SetOccurrenceTopicRequest } from "../contracts/SetOccurrenceTopicRequest";
import {
  mapResponseToOccurrenceTopic,
  mapResponseToProgress,
  mapResponseToSessionTopicPlan,
} from "../mappers/TopicPlanResponseMapper";
import { Progress } from "../models/Progress";
import { OccurrenceTopic, SessionTopicPlan, TopicRef } from "../models/TopicPlan";

/** Session topic plans, per-date topics and member Progress (US-7). */
const BASE = "/training";
type Rejection = { rejectValue: ApiErrorResponse };

/**
 * Every live topic of the organization's live programmes, in programme then curriculum
 * order — the choices for a Session's topic plan and a date's override.
 */
export const fetchTopicChoices = createAsyncThunk<TopicRef[], void, Rejection>(
  "training/fetchTopicChoices",
  async (_, { rejectWithValue }) => {
    try {
      const list = await axiosInstance.get<PaginatedResult<ProgrammeSummaryResponse>>(`${BASE}/programmes`, {
        params: { page: 1, pageSize: 100 },
      });
      const live = list.data.items.filter((p) => !p.isArchived);
      const details = await Promise.all(
        live.map((p) => axiosInstance.get<ProgrammeResponse>(`${BASE}/programmes/${p.id}`))
      );
      return details.flatMap(({ data }) =>
        data.topics
          .filter((t) => !t.isArchived)
          .map((t) => ({ topicId: t.id, programmeId: data.id, code: t.code, title: t.title, kind: t.kind }))
      );
    } catch (error) {
      return rejectWithValue(toApiError(error, "Failed to load topics"));
    }
  }
);

/** The Session's topic plan; null when it has none (404). */
export const fetchSessionTopicPlan = createAsyncThunk<SessionTopicPlan | null, string, Rejection>(
  "training/fetchSessionTopicPlan",
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<SessionTopicPlanResponse>(`${BASE}/sessions/${sessionId}/topic-plan`);
      return mapResponseToSessionTopicPlan(response.data);
    } catch (error) {
      const apiError = toApiError(error, "Failed to load the topic plan");
      return apiError.statusCode === 404 ? null : rejectWithValue(apiError);
    }
  }
);

/**
 * Saves the session form's topic draft against the id the session save returned: the
 * rotation (a one-off keeps only its first topic), or removes an emptied plan. Resolves
 * to null when the draft was left untouched. The session form awaits it before leaving.
 */
export const saveTopicPlanDraft = createAsyncThunk<
  SessionTopicPlan | null,
  { sessionId: string; oneOff: boolean },
  Rejection & { state: RootState }
>("training/saveTopicPlanDraft", async ({ sessionId, oneOff }, { getState, rejectWithValue }) => {
  const draft = getState().training.topicPlanDraft;
  if (!draft?.dirty) return null;
  const topicIds = (oneOff ? draft.topics.slice(0, 1) : draft.topics).map((topic) => topic.topicId);
  try {
    if (topicIds.length === 0) {
      if (draft.hadPlan) await axiosInstance.delete(`${BASE}/sessions/${sessionId}/topic-plan`);
      return null;
    }
    const response = await axiosInstance.put<SessionTopicPlanResponse>(`${BASE}/sessions/${sessionId}/topic-plan`, {
      topicIds,
      anchorDate: draft.anchorDate,
    });
    return mapResponseToSessionTopicPlan(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to save the topics"));
  }
});


/** The topic of one Session date; null when the date has none. */
export const fetchOccurrenceTopic = createAsyncThunk<
  OccurrenceTopic | null,
  { sessionId: string; date: string },
  Rejection
>("training/fetchOccurrenceTopic", async ({ sessionId, date }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<OccurrenceTopicResponse[]>(
      `${BASE}/sessions/${sessionId}/occurrence-topics`,
      { params: { from: date, to: date } }
    );
    const match = response.data.find((t) => t.occurrenceDate === date);
    return match ? mapResponseToOccurrenceTopic(match) : null;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load the topic"));
  }
});

/** Sets (or, with a null topic, clears) one date's topic; returns what the date teaches now. */
export const setOccurrenceTopic = createAsyncThunk<
  OccurrenceTopic | null,
  { sessionId: string; date: string; request: SetOccurrenceTopicRequest },
  Rejection
>("training/setOccurrenceTopic", async ({ sessionId, date, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<OccurrenceTopicResponse | "">(
      `${BASE}/sessions/${sessionId}/occurrence-topics/${date}`,
      request
    );
    return response.data ? mapResponseToOccurrenceTopic(response.data) : null;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to save the topic"));
  }
});

export const fetchEnrolmentProgress = createAsyncThunk<
  Progress,
  { memberId: string; enrolmentId: string },
  Rejection
>("training/fetchEnrolmentProgress", async ({ memberId, enrolmentId }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<ProgressResponse>(
      `${BASE}/members/${memberId}/enrolments/${enrolmentId}/progress`
    );
    return mapResponseToProgress(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load the progress"));
  }
});
